import { ConnectionListResult, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQuery,
  VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamDatasourcesConnection_nodes,
  VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQuery';
import {
  VirtualConnectionTableDownstreamDatasourcesQuery,
  VirtualConnectionTableDownstreamDatasourcesQuery_virtualConnectionTables_downstreamDatasourcesConnection_nodes,
  VirtualConnectionTableDownstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamDatasourcesQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQueryGql';
import {
  VirtualConnectionTableDownstreamDatasourcesQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamDatasourcesQueryGql';
import {
  DatasourcesDisplayGrid,
  DatasourcesDisplayGridRowData,
  getRowDataForDatasource,
} from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = VirtualConnectionTableDownstreamDatasourcesQueryVariables;
type QueryResults = VirtualConnectionTableDownstreamDatasourcesQuery;
type QueryResultListNode = VirtualConnectionTableDownstreamDatasourcesQuery_virtualConnectionTables_downstreamDatasourcesConnection_nodes;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamDatasourcesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  return virtualConnection ? virtualConnection.downstreamDatasourcesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.downstreamDatasourcesConnection : null;
}

export type VirtualConnectionTableDownstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionTableDownstreamDatasourcesQueryGrid(props: VirtualConnectionTableDownstreamDatasourcesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatasourcesLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamDatasourcesFilteredByColumnQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as PublishedDatasourceSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatasource}
    >
      {({ rowStore }) => <DatasourcesDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamDatasourcesQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as PublishedDatasourceSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForDatasource}
    >
      {({ rowStore }) => <DatasourcesDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
